import { Controller } from '@hotwired/stimulus'
import { post, destroy } from '../lib/request'
import { SafariImageHandler } from '../lib/safari_image_handler.js'

// Connects to data-controller="explore"
export default class extends Controller {
  static targets = [
    'image',
    'heart',
    'modal',
    'modalFrame',
    'galleryLink',
    'profileImage',
    'username',
    'productLink',
    'imageLink',
    'somethingNew',
    'moreLike',
    'link',
  ]

  heartTargetConnected = (element) => {
    element.addEventListener('click', () => {
      this.handleLikes()
    })
  }

  imageTargetConnected = (element) => {
    element.addEventListener('click', (event) => {
      this.toggleModal(event)
    })

    const adjuster = new SafariImageHandler(element)
    adjuster.init()
  }

  imageLinkTargetConnected = (element) => {
    const adjuster = new SafariImageHandler(element, true)
    adjuster.init()
  }

  initialize = () => {
    this.likes = JSON.parse(this.element.dataset.likesObject)
  }

  connect = () => {
    this.simulateLazyLoad()

    this.backgroundTarget = document.getElementById('modalBackground')

    if (this.backgroundTarget) {
      this.backgroundTarget.addEventListener('click', (event) => {
        this.toggleModal(event)
      })
    }
  }

  toggleModal = (event) => {
    if (!this.hasModalTarget) return

    event.preventDefault()

    const toShow = this.modalTarget.classList.contains('hidden')
    const link = event.target.closest('a')

    if (link && link.dataset.productJson && toShow) {
      document.addEventListener('keydown', this.handleEscKey)
      const json = JSON.parse(link.dataset.productJson)
      this.buildModal(json)
      this.element.appendChild(this.backgroundTarget)
    }
    else {
      document.removeEventListener('keydown', this.handleEscKey)
      this.modalTarget.appendChild(this.backgroundTarget)
    }

    const classNames = ['hidden', 'flex']
    classNames.forEach(className => this.modalTarget.classList.toggle(className))
    this.modalTarget.style.top = `${window.scrollY}px`
  }

  buildModal = (json) => {
    this.galleryLinkTarget.href = json.gallery_link
    this.productLinkTarget.href = json.product_link
    this.linkTarget.href = json.product_link
    this.somethingNewTarget.href = json.new_link
    this.moreLikeTarget.href = json.more_link
    this.usernameTarget.innerHTML = json.username
    this.imageLinkTarget.src = json.image_link
    this.heartTarget.dataset.productId = json.product_id

    const fill = this.likes[json.product_id] ? 'currentColor' : 'none'
    this.heartSvg.setAttribute('fill', fill)
  }

  simulateLazyLoad = () => {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target
          img.src = img.dataset.src
          observer.unobserve(img)
        }
      })
    }, {
      root: null,
      threshold: 0,
      rootMargin: '1500px 1500px 1500px 1500px',
    })
    this.imageTargets.forEach(img => observer.observe(img))
  }

  handleEscKey = (event) => {
    if (event.key === 'Escape') {
      this.toggleModal(event)
    }
  }

  async handleLikes() {
    const { productId } = this.heartTarget.dataset

    if (this.heartSvg.getAttribute('fill') === 'none') {
      const params = { product_id: productId }
      const response = await post('/likes', {
        body: JSON.stringify(params),
        responseKind: 'json',
      })

      if (response.ok) {
        const json = await response.json

        this.heartTarget.dataset.likeId = json.like_id
        this.likes[productId] = json.like_id
      }
      return this.heartSvg.setAttribute('fill', 'currentColor')
    }
    else {
      const likeId = this.likes[productId]

      if (likeId) {
        const response = await destroy(`/likes/${likeId}`, { responseKind: 'json' })

        if (response.ok) {
          this.heartSvg.setAttribute('fill', 'none')

          this.heartTarget.removeAttribute('data-like-id')
          return delete this.likes[productId]
        }
      }
    }
  }

  get heartSvg() {
    return this.heartTarget.querySelector('svg')
  }
}
